import { useRef, useState } from "react";
import { toast } from "sonner";
import { X, Paperclip } from "lucide-react";
import LightRichEditor from "@/components/admin/crm/LightRichEditor";
import { clientCreateChangeRequest, clientUploadCRDocument, apiErrorMsg } from "@/lib/api";

export default function ClientCRCreateModal({ projects, token, onClose, onCreated }) {
  const [form, setForm] = useState({
    project_id: projects[0]?.id || "",
    title: "",
    description: "",
    expectation: "",
    severity: "Minor",
  });
  const [saving, setSaving] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const fileRef = useRef(null);

  const save = async () => {
    if (!form.project_id) return toast.error("Select a project");
    if (!form.title.trim()) return toast.error("Title is required");
    // Backend caps description at 5 MB (mostly to fit pasted screenshots).
    if ((form.description || "").length > 4_500_000) {
      return toast.error("Description is too large. Please attach big screenshots as files below instead of pasting them inline.");
    }
    setSaving(true);
    try {
      const created = await clientCreateChangeRequest(token, form);
      for (const f of pendingFiles) {
        try {
          await clientUploadCRDocument(token, created.id, f);
        } catch (e) {
          toast.error(`Upload failed for ${f.name}: ${apiErrorMsg(e)}`);
        }
      }
      toast.success("Change request raised. Reachvel will get back to you.");
      onCreated();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Couldn't raise the CR"));
    } finally { setSaving(false); }
  };

  const addFile = (f) => {
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) return toast.error(`${f.name} exceeds 10MB`);
    setPendingFiles((p) => [...p, f]);
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-lg border border-black/10 shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-black/10 sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#ff5722] font-bold">Raise change request</div>
            <h3 className="text-lg font-bold text-[#0a0a0a]">{form.title || "New CR"}</h3>
          </div>
          <button onClick={onClose} data-testid="client-cr-modal-close" className="p-1.5 rounded hover:bg-black/5">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#4a4a4a] font-bold">Project *</span>
              <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })}
                data-testid="client-cr-input-project"
                className="mt-1 w-full border border-black/15 rounded-md px-3 py-2 text-sm outline-none focus:border-[#ff5722]">
                <option value="">— Select project —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#4a4a4a] font-bold">Severity</span>
              <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}
                data-testid="client-cr-input-severity"
                className="mt-1 w-full border border-black/15 rounded-md px-3 py-2 text-sm outline-none focus:border-[#ff5722]">
                <option>Minor</option><option>Major</option><option>Critical</option>
              </select>
            </label>
          </div>

          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#4a4a4a] font-bold">Title *</span>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="client-cr-input-title"
              placeholder="e.g. Change primary CTA color on home page"
              className="mt-1 w-full border border-black/15 rounded-md px-3 py-2 text-sm outline-none focus:border-[#ff5722]" />
          </label>

          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#4a4a4a] font-bold">Describe the change *</span>
            <div className="mt-1">
              <LightRichEditor
                value={form.description}
                onChange={(v) => setForm({ ...form, description: v })}
                placeholder="Describe what you'd like changed. You can paste screenshots directly."
                testid="client-cr-input-description"
              />
            </div>
          </label>

          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#4a4a4a] font-bold">Expected outcome (optional)</span>
            <textarea value={form.expectation} onChange={(e) => setForm({ ...form, expectation: e.target.value })}
              data-testid="client-cr-input-expectation"
              rows={2} placeholder="What outcome do you expect from this change?"
              className="mt-1 w-full border border-black/15 rounded-md px-3 py-2 text-sm outline-none focus:border-[#ff5722]" />
          </label>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#4a4a4a] font-bold">Attachments (optional)</span>
              <button onClick={() => fileRef.current?.click()}
                data-testid="client-cr-file-btn"
                className="inline-flex items-center gap-1 px-3 py-1 text-[11px] font-mono uppercase tracking-[0.1em] border border-black/15 rounded-full hover:border-[#ff5722] hover:text-[#ff5722]">
                <Paperclip className="h-3 w-3" /> Attach file
              </button>
              <input ref={fileRef} type="file" hidden onChange={(e) => {
                const f = e.target.files?.[0]; e.target.value = "";
                addFile(f);
              }} />
            </div>
            {pendingFiles.length === 0 ? (
              <div className="text-xs text-[#9a9a9a] italic">No files attached yet.</div>
            ) : (
              <ul className="space-y-1">
                {pendingFiles.map((f, i) => (
                  <li key={i} className="flex items-center justify-between border border-black/10 rounded px-3 py-1.5 text-xs">
                    <span className="truncate">{f.name} <span className="text-[#9a9a9a]">({Math.round(f.size/1024)} KB)</span></span>
                    <button onClick={() => setPendingFiles(pendingFiles.filter((_, j) => j !== i))}
                      className="p-1 text-rose-600 hover:bg-rose-50 rounded"><X className="h-3 w-3" /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-black/10 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 text-xs border border-black/15 rounded-full hover:bg-black/5">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="client-cr-modal-save"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs bg-[#ff5722] text-white rounded-full hover:bg-[#0a0a0a] disabled:opacity-60">
            {saving ? "Raising…" : "Raise CR"}
          </button>
        </div>
      </div>
    </div>
  );
}
